import React,{useEffect,useRef,useState,useCallback} from 'react';
import {useInView} from 'react-intersection-observer';

/* Reduced motion hook */
export function useReducedMotion(){
  const [r,setR]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(()=>{const m=window.matchMedia('(prefers-reduced-motion: reduce)');const f=()=>setR(m.matches);f();m.addEventListener?.('change',f);return()=>m.removeEventListener?.('change',f);},[]);
  return r;
}
export function useIsDesktop(){
  const [d,setD]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(min-width:1025px) and (pointer:fine)').matches);
  useEffect(()=>{const m=window.matchMedia('(min-width:1025px) and (pointer:fine)');const f=()=>setD(m.matches);f();m.addEventListener?.('change',f);return()=>m.removeEventListener?.('change',f);},[]);
  return d;
}
export function useIsMobile(){
  const [m,setM]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(max-width:768px)').matches);
  useEffect(()=>{const mq=window.matchMedia('(max-width:768px)');const f=()=>setM(mq.matches);f();mq.addEventListener?.('change',f);return()=>mq.removeEventListener?.('change',f);},[]);
  return m;
}

/* Reveal on scroll */
export function Reveal({children,as:Tag='div',delay=0,className='',...rest}){
  const {ref,inView}=useInView({triggerOnce:true,threshold:0.15,rootMargin:'0px 0px -8% 0px'});
  const dc=delay?` reveal-delay-${delay}`:'';
  return <Tag ref={ref} className={`reveal${inView?' in':''}${dc} ${className}`} {...rest}>{children}</Tag>;
}

/* CountUp */
export function CountUp({end,duration=1800,suffix='',prefix='',format=true}){
  const {ref,inView}=useInView({triggerOnce:true,threshold:0.4});
  const [val,setVal]=useState(0);const reduced=useReducedMotion();
  useEffect(()=>{
    if(!inView)return;if(reduced){setVal(end);return;}
    let raf;const start=performance.now();
    const tick=(now)=>{const p=Math.min(1,(now-start)/duration);const e=1-Math.pow(1-p,3);setVal(Math.round(end*e));if(p<1)raf=requestAnimationFrame(tick);};
    raf=requestAnimationFrame(tick);return()=>cancelAnimationFrame(raf);
  },[inView,end,duration,reduced]);
  const disp=format?val.toLocaleString('tr-TR'):val;
  return <span ref={ref}>{prefix}{disp}{suffix}</span>;
}

/* SplitText — word-by-word rise */
export function SplitText({text,className='',stagger=0.06}){
  const reduced=useReducedMotion();
  const words=text.split(' ');
  if(reduced)return <span className={className}>{text}</span>;
  return <span className={className} aria-label={text}>{words.map((w,i)=>(
    <span key={i} className="split-word" aria-hidden="true"><span style={{animationDelay:`${i*stagger}s`}}>{w}</span>{i<words.length-1?' ':''}</span>
  ))}</span>;
}

/* MaskedHeading — clip reveal on view */
export function MaskedHeading({children,className='h2'}){
  const {ref,inView}=useInView({triggerOnce:true,threshold:0.3});
  return <div ref={ref} style={{overflow:'hidden'}}>
    <div className={className} style={{transform:inView?'translateY(0)':'translateY(105%)',transition:'transform 1s cubic-bezier(.16,1,.3,1)'}}>{children}</div>
  </div>;
}

/* FoldText — 3D fold-in on view */
export function FoldText({children,className='h2'}){
  const {ref,inView}=useInView({triggerOnce:true,threshold:0.4});
  return <div ref={ref} style={{perspective:'900px'}}>
    <div className={className} style={{transformOrigin:'top',transform:inView?'rotateX(0) translateY(0)':'rotateX(-72deg) translateY(10px)',opacity:inView?1:0,transition:'transform 1s cubic-bezier(.16,1,.3,1),opacity .8s ease'}}>{children}</div>
  </div>;
}

/* GradientWaves bg */
export function GradientWaves(){
  return <div className="waves" aria-hidden="true"><span className="w1"/><span className="w2"/><span className="w3"/></div>;
}

/* SpotlightCard */
export function SpotlightCard({children,className='',...rest}){
  const ref=useRef(null);
  const onMove=useCallback((e)=>{const el=ref.current;if(!el)return;const r=el.getBoundingClientRect();el.style.setProperty('--mx',`${e.clientX-r.left}px`);el.style.setProperty('--my',`${e.clientY-r.top}px`);},[]);
  return <div ref={ref} onMouseMove={onMove} className={`spotlight ${className}`} {...rest}>{children}</div>;
}

/* SpecularButton (used via className btn btn-primary + optional spark) */
export function SpecularButton({children,onClick,spark=false,as:Tag='button',className='btn btn-primary',...rest}){
  const handle=(e)=>{if(spark)fireSpark(e.clientX,e.clientY);onClick&&onClick(e);};
  return <Tag className={className} onClick={handle} data-cursor="go" {...rest}>{children}</Tag>;
}

/* Click spark util */
export function fireSpark(x,y){
  if(window.matchMedia('(prefers-reduced-motion: reduce)').matches)return;
  for(let i=0;i<10;i++){
    const s=document.createElement('span');s.className='spark';document.body.appendChild(s);
    const ang=(Math.PI*2*i)/10;const dist=32+Math.random()*28;
    s.style.left=x+'px';s.style.top=y+'px';
    const a=s.animate([{transform:'translate(-50%,-50%) scale(1)',opacity:1},{transform:`translate(calc(-50% + ${Math.cos(ang)*dist}px),calc(-50% + ${Math.sin(ang)*dist}px)) scale(0)`,opacity:0}],{duration:520,easing:'cubic-bezier(.22,.61,.36,1)'});
    a.onfinish=()=>s.remove();
  }
}

/* MagneticButton wrapper (desktop) */
export function Magnetic({children,strength=0.3}){
  const ref=useRef(null);const inner=useRef(null);
  const desktop=useIsDesktop();
  useEffect(()=>{
    if(!desktop)return;const el=ref.current;if(!el)return;
    const move=(e)=>{const r=el.getBoundingClientRect();const x=e.clientX-(r.left+r.width/2);const y=e.clientY-(r.top+r.height/2);el.style.transform=`translate(${x*strength}px,${y*strength}px)`;if(inner.current)inner.current.style.transform=`translate(${-x*strength*0.4}px,${-y*strength*0.4}px)`;};
    const leave=()=>{el.style.transform='';if(inner.current)inner.current.style.transform='';};
    const parent=el.parentElement;parent.addEventListener('mousemove',move);parent.addEventListener('mouseleave',leave);
    return()=>{parent.removeEventListener('mousemove',move);parent.removeEventListener('mouseleave',leave);};
  },[desktop,strength]);
  return <span ref={ref} style={{display:'inline-block',transition:'transform .35s cubic-bezier(.22,.61,.36,1)',position:'relative'}}><span ref={inner} style={{display:'inline-block',transition:'transform .35s cubic-bezier(.22,.61,.36,1)'}}>{children}</span></span>;
}

/* TrueFocus — words cycle focus */
export function TrueFocus({words}){
  const {ref,inView}=useInView({threshold:0.4});
  const [active,setActive]=useState(0);const reduced=useReducedMotion();
  useEffect(()=>{if(!inView||reduced)return;const id=setInterval(()=>setActive(a=>(a+1)%words.length),1400);return()=>clearInterval(id);},[inView,words.length,reduced]);
  return <div ref={ref} className="truefocus">{words.map((w,i)=>(
    <span key={i} className={`tf-word${i===active?' tf-active':''}`} onMouseEnter={()=>setActive(i)}>{w}</span>
  ))}</div>;
}

/* ParticleText — canvas, used ONCE. Mouse repels particles. */
export function ParticleText({text='PLAZA FLOW',height=220}){
  const canvasRef=useRef(null);const wrapRef=useRef(null);
  const {ref:ioRef,inView}=useInView({threshold:0.2});
  const reduced=useReducedMotion();
  useEffect(()=>{
    if(reduced)return;const canvas=canvasRef.current;const wrap=wrapRef.current;if(!canvas||!wrap)return;
    const ctx=canvas.getContext('2d');let particles=[];let raf;let mouse={x:-9999,y:-9999};let running=false;
    const dpr=Math.min(window.devicePixelRatio||1,2);
    function build(){
      const w=wrap.clientWidth;const h=height;canvas.width=w*dpr;canvas.height=h*dpr;canvas.style.width=w+'px';canvas.style.height=h+'px';
      const off=document.createElement('canvas');off.width=w;off.height=h;const octx=off.getContext('2d');
      const fs0=Math.min(h*0.72,203);octx.font=`800 ${fs0}px Manrope, sans-serif`;
      const maxW=w*0.9;const tw=octx.measureText(text).width;
      const fs=tw>maxW?Math.max(22,fs0*maxW/tw):fs0;
      octx.fillStyle='#071B35';octx.font=`800 ${fs}px Manrope, sans-serif`;octx.textAlign='center';octx.textBaseline='middle';octx.fillText(text,w/2,h/2);
      const data=octx.getImageData(0,0,w,h).data;particles=[];const gap=Math.max(4,Math.round(w/220));
      for(let y=0;y<h;y+=gap){for(let x=0;x<w;x+=gap){const idx=(y*w+x)*4+3;if(data[idx]>128){particles.push({x:Math.random()*w,y:Math.random()*h,bx:x,by:y,vx:0,vy:0});}}}
    }
    function frame(){
      const w=canvas.width/dpr;const h=canvas.height/dpr;ctx.setTransform(dpr,0,0,dpr,0,0);ctx.clearRect(0,0,w,h);ctx.fillStyle='#071B35';
      for(const p of particles){const dx=p.x-mouse.x;const dy=p.y-mouse.y;const d=Math.hypot(dx,dy);if(d<70){const f=(70-d)/70;p.vx+=(dx/d)*f*3.2;p.vy+=(dy/d)*f*3.2;}
        p.vx+=(p.bx-p.x)*0.05;p.vy+=(p.by-p.y)*0.05;p.vx*=0.86;p.vy*=0.86;p.x+=p.vx;p.y+=p.vy;
        ctx.fillStyle=d<80?'#F7620A':'#0866D7';ctx.fillRect(p.x,p.y,1.6,1.6);}
      raf=requestAnimationFrame(frame);
    }
    build();
    const onMove=(e)=>{const r=canvas.getBoundingClientRect();mouse.x=e.clientX-r.left;mouse.y=e.clientY-r.top;};
    const onLeave=()=>{mouse.x=-9999;mouse.y=-9999;};
    const onResize=()=>build();
    canvas.addEventListener('mousemove',onMove);canvas.addEventListener('mouseleave',onLeave);window.addEventListener('resize',onResize);
    if(inView&&!running){running=true;frame();}
    return()=>{cancelAnimationFrame(raf);canvas.removeEventListener('mousemove',onMove);canvas.removeEventListener('mouseleave',onLeave);window.removeEventListener('resize',onResize);};
  },[inView,reduced,text,height]);
  return <div ref={(el)=>{wrapRef.current=el;ioRef(el);}} style={{width:'100%'}}>
    {reduced
      ? <div className="h1" style={{textAlign:'center'}}>{text}</div>
      : <canvas ref={canvasRef} aria-label={text} role="img" style={{width:'100%',height}}/>}
    <span style={{position:'absolute',width:1,height:1,overflow:'hidden',clip:'rect(0 0 0 0)'}}>{text}</span>
  </div>;
}

/* ScrollExpand — image scales up on scroll into view */
export function ScrollExpand({src,alt,caption}){
  const {ref,inView}=useInView({threshold:0.2,triggerOnce:true});
  return <figure ref={ref} className="scroll-expand">
    <img src={src} alt={alt} loading="lazy" style={{transform:inView?'scale(1)':'scale(.82)',opacity:inView?1:.4,transition:'transform 1.4s cubic-bezier(.16,1,.3,1),opacity 1s ease'}}/>
    {caption&&<figcaption>{caption}</figcaption>}
  </figure>;
}

/* AccordionGallery — horizontal expanding panels */
export function AccordionGallery({panels}){
  const [active,setActive]=useState(0);
  return <div className="accordion-gallery">
    {panels.map((p,i)=>(
      <div key={i} className={`ag-panel${i===active?' ag-active':''}`} onMouseEnter={()=>setActive(i)} onClick={()=>setActive(i)} data-cursor="see" style={{backgroundImage:`url(${p.img})`}}>
        <div className="ag-overlay"/>
        <div className="ag-content"><span className="ag-index">0{i+1}</span><h3>{p.title}</h3><p className={i===active?'ag-desc show':'ag-desc'}>{p.desc}</p></div>
      </div>
    ))}
  </div>;
}
